import React, { useEffect, useRef, useState } from "react";
import { loadYouTubeApi } from "../../utils/thirdPartyScripts";

const YouTubeIframe = ({
  src,
  style,
  title,
  id,
  setVideoData,
  resizeDimensions,
  dimensions,
  actionCableVideoMontior,
  editortype,
  allowInteraction,
  ...props
}) => {
  const videoId = src?.split("/")[4]?.split("?")[0] || "";
  const iframeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const logCountRef = useRef(0); // Separate ref for log counting
  const playerCreatedRef = useRef(false); // Track if player was created
  const iframeLoadedRef = useRef(false); // Track if iframe is loaded
  const createPlayerRef = useRef(null); // Store createPlayer function

  useEffect(() => {
    // Always log initialization
    console.log("========================================");
    console.log("[YT] YOUTUBE IFRAME INITIALIZING");
    console.log("[YT] videoId:", videoId);
    console.log("[YT] src:", src);
    console.log("[YT] element id:", id);
    console.log("[YT] hasCallback:", !!actionCableVideoMontior);
    console.log("========================================");
    
    if (!videoId) {
      console.error("[YT] ERROR: Invalid videoId:", videoId, "from src:", src);
      return;
    }

    // Check if callback is available
    if (!actionCableVideoMontior) {
      console.warn("[YT]   WARNING: actionCableVideoMontior callback not provided for video:", id);
    }

    // Function to create the player - only call when iframe is loaded
    const createPlayer = () => {
      if (playerCreatedRef.current) {
        console.log("[YT] Player already created, skipping");
        return;
      }
      
      console.log("[YT] ===== CREATING PLAYER ===== ");
      console.log("[YT] window.YT exists:", !!window.YT);
      console.log("[YT] window.YT.Player exists:", !!(window.YT && window.YT.Player));
      console.log("[YT] iframeRef.current exists:", !!iframeRef.current);
      console.log("[YT] iframeLoadedRef:", iframeLoadedRef.current);
      
      if (window.YT && window.YT.Player && iframeRef.current && iframeLoadedRef.current) {
        console.log("[YT]    All conditions met, creating player for:", videoId, "element:", id);
        
        try {
          playerCreatedRef.current = true;
          const player = new window.YT.Player(iframeRef.current, {
            videoId,
            playerVars: {
              'enablejsapi': 1,
              'origin': window.location.origin
            },
            events: {
              onReady: (event) => {
                console.log("[YT] ✅✅✅ PLAYER READY EVENT FIRED ✅✅✅");
                console.log("[YT] videoId:", videoId, "element:", id);
                console.log("[YT] event.target:", event.target);
                console.log("[YT] Player ready - checking playerInfo:", {
                  hasPlayerInfo: !!event.target.playerInfo,
                  currentTime: event.target.playerInfo?.currentTime,
                  duration: event.target.playerInfo?.duration
                });
              },
              onStateChange: (event) => {
                console.log("[YT]    STATE CHANGE EVENT FIRED 🔴🔴");
                console.log("[YT] State:", event.data, "for element:", id);
                console.log("[YT] State meanings: -1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering, 5=cued");
                handlePlayerStateChange(event, player);
              },
              onError: (event) => {
                console.error("[YT] Player error event:", event.data);
                handlePlayerError(event);
              },
            },
          });
          console.log("[YT] Player object created:", player);
          console.log("[YT] Player type:", typeof player);
          console.log("[YT] Player has playerInfo:", !!player.playerInfo);
          
          // Store player on iframe for later access
          if (iframeRef.current) {
            iframeRef.current.player = player;
          }
        } catch (error) {
          console.error("[YT] ERROR creating player:", error);
          console.error("[YT] Error details:", error.message, error.stack);
          playerCreatedRef.current = false; // Reset on error
        }
      } else {
        console.warn("[YT]   Cannot create player yet:", {
          hasYT: !!window.YT,
          hasPlayer: !!(window.YT && window.YT.Player),
          hasIframe: !!iframeRef.current,
          iframeLoaded: iframeLoadedRef.current
        });
      }
    };
    
    // Store createPlayer in ref so handleIframeLoad can access it
    createPlayerRef.current = createPlayer;

    // Function to handle the YouTube Iframe API loading
    const onYouTubeIframeAPIReady = () => {
      console.log("[YT] ===== onYouTubeIframeAPIReady CALLED =====");
      // Try to create player, but it will wait for iframe to load
      if (iframeLoadedRef.current && createPlayerRef.current) {
        createPlayerRef.current();
      } else {
        console.log("[YT] Waiting for iframe to load before creating player");
        console.log("[YT] iframeLoaded:", iframeLoadedRef.current, "createPlayer available:", !!createPlayerRef.current);
      }
    };

    const handlePlayerStateChange = (event, player) => {
      let id = event.target.h.id;
      if (id === undefined) {
        id = event.target.g.id;
      }

      // Use event.target as the player instance (it's the actual player that updates)
      const playerInstance = event.target;

      switch (event.data) {
        case 1: {
          console.log("[YT] Video started playing, element id:", id);
          
          // Track video play as a click event - this is critical for tracking
          // YouTube iframes don't allow click events, so we trigger on play
          // This ensures "user clicked video" is recorded even if they don't click directly
          if (props.onClick && typeof props.onClick === 'function') {
            try {
              // Create a proper synthetic event that matches what elemClick expects
              // The onClick handler in Element.js calls: elemClick(element?.id, event, element)
              // So we need to pass an event object that won't cause issues
              const syntheticEvent = {
                target: { id: id },
                currentTarget: { id: id },
                type: 'play',
                stopPropagation: () => {},
                preventDefault: () => {},
                // Make sure delegateTarget doesn't have accept button
                delegateTarget: { children: [] }
              };
              
              console.log("[YT] Triggering onClick/elemClick for video play, element id:", id, "onClick type:", typeof props.onClick);
              props.onClick(syntheticEvent);
              console.log("[YT] onClick triggered successfully for element:", id);
            } catch (e) {
              console.error("[YT] Error triggering onClick:", e, e.stack);
            }
          } else {
            console.warn("[YT] onClick handler not available for element:", id, {
              hasOnClick: !!props.onClick,
              onClickType: typeof props.onClick
            });
          }
          
          const data = {
            id: id,
          };
          
          // Clear any existing interval
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          
          timerRef.current = setInterval(() => {
            if (window?.objInit?.tlb?.disable_settings?.disable_on === "cyoa") {
              const playerElement = document.getElementById(id);
              const iframeRect = playerElement.getBoundingClientRect();
              const isInsideViewport =
                iframeRect.top < window.innerHeight &&
                iframeRect.bottom > 0 &&
                iframeRect.left < window.innerWidth &&
                iframeRect.right > 0;
              if (!isInsideViewport) {
                playerInstance.pauseVideo();
              }
            }
            
            // FIX: Use event.target (playerInstance) instead of player from closure
            // playerInstance.playerInfo updates in real-time, player.playerInfo does not
            let currentTime = 0;
            let duration = 0;
            let methodUsed = 'none';
            
            // Increment log counter
            logCountRef.current = (logCountRef.current || 0) + 1;
            
            // Debug: Check what's available on playerInstance (first time only)
            if (logCountRef.current === 1) {
              console.log('[YT] ===== PLAYER INSTANCE CHECK =====');
              console.log('[YT] Player instance:', playerInstance);
              console.log('[YT] Has playerInfo:', !!playerInstance.playerInfo);
              if (playerInstance.playerInfo) {
                console.log('[YT] playerInfo object:', playerInstance.playerInfo);
                console.log('[YT] playerInfo.currentTime:', playerInstance.playerInfo.currentTime);
                console.log('[YT] playerInfo.duration:', playerInstance.playerInfo.duration);
                console.log('[YT] playerInfo keys:', Object.keys(playerInstance.playerInfo));
              }
              console.log('[YT] Has getCurrentTime:', typeof playerInstance.getCurrentTime === 'function');
              console.log('[YT] Has getDuration:', typeof playerInstance.getDuration === 'function');
              console.log('[YT] Player keys:', Object.keys(playerInstance).slice(0, 15));
              console.log('[YT] ===================================');
            }
            
            // Try API methods first (more reliable)
            if (typeof playerInstance.getCurrentTime === 'function' && 
                typeof playerInstance.getDuration === 'function') {
              try {
                currentTime = playerInstance.getCurrentTime();
                duration = playerInstance.getDuration();
                methodUsed = 'API';
                
                if (logCountRef.current <= 3) {
                  console.log('[YT] API Methods Result:', { currentTime, duration, methodUsed });
                }
              } catch (e) {
                console.warn('[YT] Error calling API methods:', e);
                // Fall through to playerInfo
              }
            }
            
            // Fallback to playerInfo if API methods failed or returned invalid values
            if ((!currentTime && !duration) || (currentTime === 0 && duration === 0)) {
              if (playerInstance.playerInfo) {
                const pi = playerInstance.playerInfo;
                
                if (logCountRef.current <= 3) {
                  console.log('[YT] Trying playerInfo fallback:', {
                    hasCurrentTime: pi.currentTime !== undefined,
                    currentTimeValue: pi.currentTime,
                    hasDuration: pi.duration !== undefined,
                    durationValue: pi.duration
                  });
                }
                
                if (pi.currentTime !== undefined && pi.currentTime !== null) {
                  currentTime = parseFloat(pi.currentTime) || 0;
                  methodUsed = 'playerInfo';
                }
                if (pi.duration !== undefined && pi.duration !== null) {
                  duration = parseFloat(pi.duration) || 0;
                }
                
                if (logCountRef.current <= 3) {
                  console.log('[YT] playerInfo Result:', { currentTime, duration, methodUsed });
                }
              } else {
                if (logCountRef.current <= 3) {
                  console.warn('[YT] playerInfo not available on playerInstance');
                }
              }
            }
            
            // Final validation with detailed logging
            if (!currentTime && !duration) {
              if (logCountRef.current <= 5) {
                console.error('[YT] ===== CANNOT GET VIDEO TIME =====');
                console.error('[YT] All methods failed!');
                console.error('[YT] playerInstance:', playerInstance);
                console.error('[YT] playerInfo:', playerInstance.playerInfo);
                console.error('[YT] getCurrentTime type:', typeof playerInstance.getCurrentTime);
                console.error('[YT] getDuration type:', typeof playerInstance.getDuration);
                console.error('[YT] ===================================');
              }
              return;
            }
            
            // Log raw values for debugging
            if (logCountRef.current <= 5) {
              console.log('[YT] Raw values retrieved:', {
                currentTime,
                duration,
                methodUsed,
                currentTimeType: typeof currentTime,
                durationType: typeof duration
              });
            }
            
            // Skip if duration is 0 (metadata not loaded yet)
            if (!duration || duration === 0) {
              return;
            }
            
            // Calculate watched metrics
            // For watchedSeconds, use Math.ceil to ensure we get at least 1 second if video is playing
            // This ensures we don't send 0 when video is actually playing
            const watchedSeconds = currentTime > 0 ? Math.max(1, Math.ceil(currentTime)) : 0;
            
            // For watchedPercent, calculate accurately but ensure minimum 1% if video is playing
            let watchedPercent = Math.round((currentTime / duration) * 100);
            // If video is playing (currentTime > 0) and percent is 0, set to 1% minimum
            if (currentTime > 0 && watchedPercent === 0) {
              watchedPercent = 1;
            }
            
            // Always send data (even if 0, but log for debugging)
            data.watchedPercent = watchedPercent;
            data.watchedSeconds = watchedSeconds;
            data.duration = Math.floor(duration);
            
            // Debug logging (first 5 ticks and then every 20th tick)
            if (logCountRef.current <= 5 || logCountRef.current % 20 === 0) {
              console.log('[YT] ===== VIDEO TRACKING DATA =====');
              console.log('[YT] Element ID:', id);
              console.log('[YT] Current Time:', currentTime.toFixed(2), 'seconds');
              console.log('[YT] Duration:', duration.toFixed(2), 'seconds');
              console.log('[YT] Watched Seconds:', watchedSeconds);
              console.log('[YT] Watched Percent:', watchedPercent + '%');
              console.log('[YT] Method Used:', methodUsed);
              console.log('[YT] ================================');
            }
            
            if (actionCableVideoMontior) {
              // Log first few calls to verify
              if (logCountRef.current <= 3) {
                console.log("[YT] ===== CALLING actionCableVideoMontior =====");
                console.log("[YT] Data being sent:", data);
                console.log("[YT] watchedSeconds:", data.watchedSeconds);
                console.log("[YT] watchedPercent:", data.watchedPercent);
                console.log("[YT] duration:", data.duration);
                console.log("[YT] ============================================");
              }
              actionCableVideoMontior(data);
            } else {
              if (logCountRef.current <= 3) {
                console.error("[YT] ===== ERROR: actionCableVideoMontior NOT AVAILABLE =====");
                console.error("[YT] Data that would be sent:", data);
                console.error("[YT] ========================================================");
              }
            }
            setIsPlaying(true);
          }, 250);
          break;
        }
        case 0: {
          const data = {
            id: id,
          };
          clearInterval(timerRef.current);
          
          // Use playerInstance (event.target) instead of player
          let currentTime = 0;
          let duration = 0;
          
          if (playerInstance.playerInfo && playerInstance.playerInfo.currentTime !== undefined) {
            currentTime = parseInt(playerInstance.playerInfo.currentTime) || 0;
            duration = parseInt(playerInstance.playerInfo.duration) || 0;
          } else if (typeof playerInstance.getCurrentTime === 'function') {
            try {
              currentTime = playerInstance.getCurrentTime();
              duration = playerInstance.getDuration();
            } catch (e) {
              console.warn('[YT] Error on ended:', e);
            }
          }
          
          data.watchedPercent = 100;
          data.watchedSeconds = Math.floor(currentTime || duration);
          data.duration = Math.floor(duration);
          
          if (actionCableVideoMontior) {
            actionCableVideoMontior(data);
          }
          setIsPlaying(false);
          break;
        }
        default:
          clearInterval(timerRef.current);
          break;
      }
    };

    const handlePlayerError = (event) => {
      console.error("YouTube Player Error: ", event.data);
      switch (event.data) {
        case 2:
          console.error("Invalid parameter value in videoId or API request.");
          break;
        case 5:
          console.error("HTML5 player error. Issue with video playback.");
          break;
        case 100:
          console.error(
            "Video not found. The videoId may be incorrect or removed."
          );
          break;
        case 101:
        case 150:
          console.error("Embedding not allowed for this video.");
          break;
        default:
          console.error("Unknown error.");
          break;
      }
    };

    let cancelled = false;
    let readyTimeout = null;
    loadYouTubeApi().then(() => {
      if (cancelled) return;
      readyTimeout = setTimeout(onYouTubeIframeAPIReady, 300);
    });

    return () => {
      cancelled = true;
      clearTimeout(readyTimeout);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (iframeRef.current && iframeRef.current.player) {
        try {
          iframeRef.current.player.destroy();
        } catch (e) {
          console.warn('[YT] Error destroying player:', e);
        }
      }
    };
  }, [videoId]); // Removed isPlaying from dependencies to prevent re-initialization

  // Pause this video whenever CYOA page navigation fires
  useEffect(() => {
    const handleCyoaPageChange = () => {
      const player = iframeRef.current?.player;
      if (player && typeof player.pauseVideo === 'function') {
        try { player.pauseVideo(); } catch (e) { console.warn('[YT] Error pausing player:', e); }
      }
    };
    document.addEventListener('cyoa-page-change', handleCyoaPageChange);
    return () => document.removeEventListener('cyoa-page-change', handleCyoaPageChange);
  }, []);

  // Handle click - YouTube iframes block clicks, but we'll try anyway
  // Main tracking happens on video play (see handlePlayerStateChange)
  const handleIframeClick = (e) => {
    console.log("[YT] Iframe wrapper clicked, element id:", id);
    if (props.onClick) {
      // Create proper event object for elemClick
      const clickEvent = {
        ...e,
        target: { id: id, ...e.target },
        currentTarget: { id: id, ...e.currentTarget },
        stopPropagation: e.stopPropagation || (() => {}),
        preventDefault: e.preventDefault || (() => {})
      };
      props.onClick(clickEvent);
    }
  };

  // Handle iframe load - critical for player initialization
  const handleIframeLoad = () => {
    console.log("[YT] ===== IFRAME LOADED ===== ");
    console.log("[YT] Element id:", id);
    console.log("[YT] Iframe ref:", iframeRef.current);
    
    iframeLoadedRef.current = true;
    
    // Wait a bit for iframe content to be ready, then create player
    setTimeout(() => {
      console.log("[YT] Iframe loaded, attempting to create player");
      if (window.YT && window.YT.Player && createPlayerRef.current) {
        createPlayerRef.current();
      } else {
        console.log("[YT] YT API not ready yet or createPlayer not available");
        console.log("[YT] Will wait for onYouTubeIframeAPIReady or retry");
        // Retry after a bit
        setTimeout(() => {
          if (createPlayerRef.current) {
            createPlayerRef.current();
          }
        }, 1000);
      }
    }, 500);
  };

  // Extract style and other props, but don't pass onClick to iframe (it doesn't work)
  const { onClick, ...iframeProps } = props;

  return (
    <iframe
      ref={iframeRef}
      style={{ display: "block", border: "none", ...style, pointerEvents: editortype === 'editor' && !allowInteraction ? "none" : "auto" }}
      onClick={handleIframeClick}
      onLoad={handleIframeLoad}
      src={`${src}${src?.includes("?") ? "&" : "?"}enablejsapi=1`}
      title={title}
      id={id}
      webkitallowfullscreen=""
      mozallowfullscreen=""
      allowFullScreen=""
      {...iframeProps}
    ></iframe>
  );
};

export default React.memo(YouTubeIframe);
